import type { ICommandBarItemProps, IContextualMenuItem } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPContextualMenuItemType } from 'baseComponents/ContextualMenu/IPContextualMenu';
import AssessmentStartCard from 'components/AuditCriteria/AssessmentStartCard';
import AuditCriteriaEditorModal from 'components/AuditCriteria/AuditCriteriaEditorModal';
import { AuditCriteriaLayerCommand, AuditCriteriaLayerDialogs } from 'components/AuditCriteria/AuditCriteriaLayerHeader';
import { viewIcon } from 'globalStyles';
import { AuditCriteriaLayer } from 'models/auditCriteriaGeneric';
import { isAuditCriteriaPreviewEnabled } from 'models/copilot/copilotPreviewFeatureToggles';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiQueryAuditCriteriaStatus } from 'services/Api/auditCriteriaService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';

interface IAssessmentItem {
  id: number;
  name: string | undefined;
}

interface IAssessmentCommand {
  assessItem: ICommandBarItemProps | undefined;
  assessDialog: JSX.Element | null;
}

// Where the item's criteria come from, read when the Assess menu opens; per item, so Previous and Next read again
interface IItemCriteria {
  entityId: number;
  layer: AuditCriteriaLayer | undefined;
  isLoading: boolean;
}

/**
 * "Assess" on an item's dossier (PLAN-075, spec 5.2 and 6.2; Chris's walk ruling, Task 30): one command with a menu.
 * "Show criteria" opens the criteria the item reads in the editor, read only and without starting a re-evaluation, for
 * every reader (PLAN-CTI-098 spec 10). "Create new task" opens the assessment start card; "Create specific criteria" copies the generic set the item reads
 * (the editor's "Change for this item", with its confirmation) and opens the item's criteria editor, and on an item
 * with criteria of its own it opens that editor ("Edit specific criteria"). Without a set the entry is disabled and
 * says why; the copy is a manager's (the endpoint's RequireManager), so the entry is not offered to anyone else, as the
 * editor's header offers its copy only to a writer. The host puts the item on its bar before Previous and Next and
 * renders the dialogs outside its stacks. Undefined for an item not saved yet and without the Audit preview.
 */
export const useAssessmentCommand = (
  entityType: number,
  entityId: number | undefined,
  entityName: string | undefined,
): IAssessmentCommand => {
  const { t } = useTranslation(['criteria', 'exceptions', 'translation']);
  const appContext = useContext(AppContext);
  const items = entityId !== undefined && entityId > 0 ? [{ id: entityId, name: entityName }] : [];
  const { assessItem: newTaskItem, assessDialog: startDialog } = useAssessmentItems(
    entityType,
    items,
    'assess',
    t('criteria:Generic.Layer.AssessCommand'),
  );
  const [criteria, setCriteria] = useState<IItemCriteria | undefined>(undefined);
  const [layerCommand, setLayerCommand] = useState<AuditCriteriaLayerCommand | undefined>(undefined);
  const [isEditorOpen, setIsEditorOpen] = useState<boolean>(false);
  const [isViewerOpen, setIsViewerOpen] = useState<boolean>(false);

  const isManager = hasUserFeatureGenericManager(appContext);
  // a read of an earlier item (Previous, Next) does not count for this one
  const current = criteria?.entityId === entityId ? criteria : undefined;

  const loadCriteria = async () => {
    if (!entityId || (current && (current.isLoading || current.layer !== undefined))) return;

    setCriteria({ entityId, layer: undefined, isLoading: true });
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const [status] = await apiQueryAuditCriteriaStatus([{ entityType, entityId }], accessToken);
      // a control with child items carries no criteria: as an item without a set
      const layer = status && status.isLeaf && status.hasCriteria ? status.layer : AuditCriteriaLayer.None;
      setCriteria({ entityId, layer, isLoading: false });
    } catch (err) {
      setCriteria(undefined);
      appContext.setError(err);
    }
  };

  const setLayer = (layer: AuditCriteriaLayer) => {
    if (entityId) setCriteria({ entityId, layer, isLoading: false });
  };

  // why an entry on the item's criteria is disabled: no set, or the read is still on its way
  const getLayerTitle = (layer: AuditCriteriaLayer | undefined): string | undefined =>
    layer === AuditCriteriaLayer.None
      ? t('exceptions:Audit.NoCriteriaSet')
      : layer === undefined
      ? t('translation:General.Notifications.Loading')
      : undefined;

  const getShowCriteriaItem = (): IContextualMenuItem => {
    const layer = current?.layer;

    return {
      key: 'showCriteria',
      text: t('criteria:Generic.Assess.ShowCriteria'),
      iconProps: viewIcon,
      disabled: layer === undefined || layer === AuditCriteriaLayer.None,
      title: getLayerTitle(layer),
      onClick: () => setIsViewerOpen(true),
    };
  };

  const getCriteriaItem = (): IContextualMenuItem => {
    const layer = current?.layer;
    const isOwn = layer === AuditCriteriaLayer.Specific;
    const isGeneric = layer === AuditCriteriaLayer.Family || layer === AuditCriteriaLayer.Subtype;

    return {
      key: 'specificCriteria',
      text: isOwn ? t('criteria:Generic.Assess.EditCriteria') : t('criteria:Generic.Assess.CreateCriteria'),
      iconProps: { iconName: isOwn ? 'Edit' : 'Copy' },
      disabled: !isOwn && !isGeneric,
      title: getLayerTitle(layer),
      onClick: () => {
        if (isOwn) {
          setIsEditorOpen(true);
        } else if (isGeneric) {
          setLayerCommand('copy');
        }
      },
    };
  };

  const assessItem: ICommandBarItemProps | undefined = newTaskItem
    ? {
        key: 'assess',
        text: t('criteria:Generic.Layer.AssessCommand'),
        iconProps: { iconName: 'ComplianceAudit' },
        subMenuProps: {
          onMenuOpened: () => {
            void loadCriteria();
          },
          items: [
            getShowCriteriaItem(),
            {
              key: 'newTask',
              text: t('criteria:Generic.Assess.NewTask'),
              iconProps: { iconName: 'TaskManager' },
              onClick: () => {
                newTaskItem.onClick?.();
              },
            },
            ...(isManager
              ? [{ key: 'divider', itemType: IPContextualMenuItemType.Divider }, getCriteriaItem()]
              : []),
          ],
        },
      }
    : undefined;

  const assessDialog =
    entityId !== undefined && entityId > 0 ? (
      <>
        {startDialog}
        <AuditCriteriaLayerDialogs
          command={layerCommand}
          entityType={entityType}
          entityId={entityId}
          entityName={entityName}
          onChanged={(set) => {
            //the copy made the item's own set: its editor opens on it
            setLayer(set.layer);
            setIsEditorOpen(true);
          }}
          onClose={() => setLayerCommand(undefined)}
        />
        {isViewerOpen && (
          <AuditCriteriaEditorModal
            isOpen={true}
            entityType={entityType}
            entityId={entityId}
            entityName={entityName}
            readonly={true}
            queueReevaluation={false}
            usageMode="modal"
            onClose={() => setIsViewerOpen(false)}
          />
        )}
        {isEditorOpen && (
          <AuditCriteriaEditorModal
            isOpen={true}
            entityType={entityType}
            entityId={entityId}
            entityName={entityName}
            usageMode="modal"
            onChanged={(set) => setLayer(set.layer)}
            onClose={() => setIsEditorOpen(false)}
          />
        )}
      </>
    ) : null;

  return { assessItem, assessDialog };
};

/**
 * "Create assessment tasks" on a list (PLAN-075, spec 6.2): the same start card for the selected items, one task per
 * item. Undefined without a selection and without the Audit feature.
 */
export const useBulkAssessmentCommand = (entityType: number, items: IAssessmentItem[]): IAssessmentCommand => {
  const { t } = useTranslation(['criteria']);

  return useAssessmentItems(entityType, items, 'assessbulk', t('criteria:Generic.Start.BulkCommand'));
};

const useAssessmentItems = (
  entityType: number,
  items: IAssessmentItem[],
  key: string,
  text: string,
): IAssessmentCommand => {
  const appContext = useContext(AppContext);
  const [dialogItems, setDialogItems] = useState<IAssessmentItem[] | undefined>(undefined);

  const available = items.length > 0 && isAuditCriteriaPreviewEnabled(appContext);

  // the dialog keeps the items it opened with: a selection that changes behind it does not change the card
  const assessItem: ICommandBarItemProps | undefined = available
    ? {
        key,
        text,
        iconProps: { iconName: 'ComplianceAudit' },
        onClick: () => setDialogItems(items),
      }
    : undefined;

  const names = new Map<number, string>();
  dialogItems?.forEach((item) => {
    if (item.name) names.set(item.id, item.name);
  });

  const assessDialog = dialogItems ? (
    <AssessmentStartCard
      entityType={entityType}
      entityIds={dialogItems.map((item) => item.id)}
      entityNames={names.size > 0 ? names : undefined}
      onClose={() => setDialogItems(undefined)}
    />
  ) : null;

  return { assessItem, assessDialog };
};
